<template>
  <nav
    :class="{
      'side-bar': true,
      'side-bar--minimized': isSideBarMinimized,
      'side-bar--hovered': isHovered || shouldShowFavoriteSearchBalloon,
    }"
  >
    <WrapperForFavoriteSearch @favorite-searches-changed="onFavoriteSearchesChanged" />
    <div class="side-bar__items" @mouseenter="setHoverState(true)" @mouseleave="setHoverState(false)">
      <div class="side-bar__home-item">
        <SideBarItem :show-minimized="showMinimized" icon="home" :text="t('home')" @click="goToV1Page('/')" />
      </div>

      <SideBarGroup
        v-for="{ category, items } in categories"
        :key="category"
        :show-minimized="showMinimized"
        :title="category"
      >
        <template v-for="(item, index) in items" :key="index">
          <SideBarItemExpandable
            v-if="!item.hidden && item.subitems"
            :is-expanded="getIsExpanded(item.storageKey)"
            :show-minimized="showMinimized"
            :subitems="item.subitems.filter((item) => !item.hidden)"
            :icon="item.icon"
            :text="item.text"
            :active-route="(activeRouteName as string)"
            @click-subitem="(subitem: Item) => goToPage({ routeName: subitem.routeName, v1Url: subitem.v1Url })"
            @toggle-subitems="() => onToggleSubItems(item.storageKey)"
          />
          <SideBarItem
            v-else-if="!item.hidden"
            :show-minimized="showMinimized"
            :icon="item.icon"
            :text="item.text"
            :disabled="item.disabled"
            :is-icon-branches="item.icon === 'branches'"
            :active="item.routeName !== undefined && activeRouteName === item.routeName"
            @click="goToPage({ routeName: item.routeName, v1Url: item.v1Url })"
          />
        </template>
      </SideBarGroup>
    </div>

    <div class="side-bar__footer">
      <div v-if="!showMinimized">© COPYRIGHT beBit, Inc.</div>
    </div>
  </nav>
  <SavedSearchBalloon
    v-if="shouldShowFavoriteSearchBalloon"
    data-cy="saved-search-balloon"
    :favorite-search="hoveredSearch"
    :adjust="balloonPosition.arrowAdjust"
    class="favorite-search-balloon"
    :style="style()"
    @mouseenter="onMouseEnterSavedSearchBalloon"
    @mouseleave="onMouseLeaveSavedSearchBalloon"
  />
</template>

<script setup lang="ts">
import { routeStore } from "@bebit/store";
// eslint-disable-next-line import/no-unresolved
import SavedSearchBalloon from "search/SavedSearchBalloon";
// eslint-disable-next-line import/no-unresolved
import WrapperForFavoriteSearch from "search/WrapperForFavoriteSearch";
import { FavoriteSearch } from "search/favoriteSearch";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import SideBarGroup from "@host/components/side-bar/SideBarGroup.vue";
import SideBarItem from "@host/components/side-bar/SideBarItem.vue";
import SideBarItemExpandable from "@host/components/side-bar/SideBarItemExpandable.vue";
import { useFavoriteSearchBalloon } from "@host/composables/storages/useFavoriteSearchBalloon";
import { useSideBar } from "@host/composables/useSideBar";
import { sideBarStore } from "@host/store/side-bar";
import { Item, StorageKey } from "@host/types/side-bar/SideBar.type";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { setAllowRouterReplace } = routeStore();
const { isSideBarMinimized, isAnalysisExpanded, isPinExpanded, setIsAnalysisExpanded, setIsPinExpanded } =
  sideBarStore();
const {
  shouldShowFavoriteSearchBalloon,
  hoveredSearch,
  balloonPosition,
  style,
  onMouseEnterSavedSearchBalloon,
  onMouseLeaveSavedSearchBalloon,
} = useFavoriteSearchBalloon();

const isHovered = ref(false);

const { categories, setFavoriteSearches } = useSideBar();

const activeRouteName = computed(() => route.meta.activeRouteName || route.name);
const showMinimized = computed(
  () => isSideBarMinimized.value && !isHovered.value && !shouldShowFavoriteSearchBalloon.value
);

onMounted(() => {
  if (route.name === "Stats") {
    setIsAnalysisExpanded(true);
  }
});

function setHoverState(hovered: boolean) {
  if (!isSideBarMinimized.value) return;
  isHovered.value = hovered;
}

function goToV1Page(url: string) {
  const v1Origin = window.location.origin;
  window.open(v1Origin + url, "_self");
}

interface RouteUrl {
  routeName?: string;
  v1Url?: string;
}

function goToPage({ routeName, v1Url }: RouteUrl) {
  if (routeName) {
    setAllowRouterReplace(false);
    router.push({ name: routeName });
  } else if (v1Url) goToV1Page(v1Url);
}

function getIsExpanded(storageKey: StorageKey | undefined): boolean {
  switch (storageKey) {
    case "expandable_analysis":
      return isAnalysisExpanded.value;
    case "expandable_pin":
      return isPinExpanded.value;
    default:
      return false;
  }
}

function onToggleSubItems(storageKey: StorageKey | undefined) {
  if (storageKey === "expandable_analysis") {
    setIsAnalysisExpanded(!isAnalysisExpanded.value);
  } else if (storageKey === "expandable_pin") {
    setIsPinExpanded(!isPinExpanded.value);
  }
}

function onFavoriteSearchesChanged(newFavoriteSearches: FavoriteSearch[]) {
  setFavoriteSearches(newFavoriteSearches);
}
</script>

<style lang="scss" scoped>
.side-bar {
  z-index: map.get($z-indexes, "side-bar");
  background: $colorWhite;
  box-shadow: 1px 0 4px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  left: 0;
  position: sticky;
  top: 70px;
  height: calc(100vh - 70px);
  transition: width 0.15s ease-out, margin-left 0.15s ease-out;
  width: $sideNavigationExpandedWidth;

  &--minimized {
    transition: width 0.1s ease-out, margin-left 0.1s ease-out;
    width: $sideNavigationMinimizedWidth;
  }

  &--hovered {
    box-shadow: 1px 0px 8px rgba($colorGray1000, 0.12);
    width: $sideNavigationExpandedWidth;
  }
}

.side-bar__items {
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
  scrollbar-width: none !important; /* Firefox */

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none !important;
  }
}

.side-bar__home-item {
  padding-top: 10px;
}

.side-bar__footer {
  align-items: start;
  border-top: 1px solid $colorBase500;
  color: $colorBase700;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  height: 73px;
  padding: 22px 0 40px 22px;
  white-space: nowrap;
}

.favorite-search-balloon {
  position: fixed;
  z-index: map.get($z-indexes, "modal");
}
</style>

<i18n lang="yaml">
en:
  home: Home
ja:
  home: ホーム
zh-hant:
  home: 主屏幕
zh-hans:
  home: 主屏幕
</i18n>
